/**
 * SFrame222 — Specific bequests (Stream J). Persists rows to sframe-222.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { SettlementStepPage } from '../../settlement';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useParams } from 'react-router-dom';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';

const step = SETTLEMENT_PHASE_CONFIGS.distribute.steps.find((s) => s.id === 'dist-2')!;

const METHOD_OPTS = ['In-kind', 'Cash equivalent', 'Trust distribution', 'Charitable residue', 'Other'] as const;

type Row = {
  id: string;
  description: string;
  beneficiaryName: string;
  amountLabel: string;
  method: (typeof METHOD_OPTS)[number];
  fulfilled: boolean;
};

interface S222Persist {
  version: 2;
  rows: Row[];
}

function coerce(raw: unknown): S222Persist {
  const fallback: S222Persist = { version: 2, rows: [] };
  if (!raw || typeof raw !== 'object') return fallback;
  const o = raw as { rows?: unknown };
  const parsed: Row[] = [];
  const arr = Array.isArray(o.rows) ? o.rows : [];
  arr.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    const r = item as Record<string, unknown>;
    const method = METHOD_OPTS.includes(r.method as (typeof METHOD_OPTS)[number])
      ? (r.method as (typeof METHOD_OPTS)[number])
      : 'Other';
    parsed.push({
      id: typeof r.id === 'string' && r.id.trim() ? r.id : `beq-${idx}`,
      description: String(r.description ?? ''),
      beneficiaryName: String(r.beneficiaryName ?? ''),
      amountLabel: String(r.amountLabel ?? ''),
      method,
      fulfilled: !!r.fulfilled,
    });
  });
  return { ...fallback, rows: parsed };
}

export default function SFrame222() {
  const { estateId: ei } = useParams<{ estateId: string }>();
  const estateId = ei ?? null;
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId, { readOnly: isArchived });

  const [rows, setRows] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    if (!estateId) {
      setRows([]);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const persisted = await loadFrameData('sframe-222');
      if (!cancelled) setRows(coerce(persisted?.data).rows);
      if (!cancelled) setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  useEffect(() => {
    if (!estateId || isArchived || !loaded) return;
    const persist: S222Persist = { version: 2, rows };
    const tm = window.setTimeout(() => void saveFrameData('sframe-222', persist as unknown as Record<string, unknown>), 450);
    return () => window.clearTimeout(tm);
  }, [rows, estateId, isArchived, loaded, saveFrameData]);

  const mutate = useCallback(
    (id: string, patch: Partial<Row>) =>
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r))),
    []
  );

  const addRow = useCallback(() => {
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `bequest-${Math.random().toString(36).slice(2, 9)}`;
    setRows((prev) =>
      prev.concat({
        id,
        description: '',
        beneficiaryName: '',
        amountLabel: '',
        method: METHOD_OPTS[0],
        fulfilled: false,
      })
    );
  }, []);

  const removeRow = useCallback((id: string) => setRows((prev) => prev.filter((r) => r.id !== id)), []);

  return (
    <SettlementStepPage
      step={step}
      phase="distribute"
      overviewRoute="/sframe-220"
      plannerFrameIds={['frame-246']}
      settlementDocumentContext={{ phase: 'distribute', stepId: 'dist-2' }}
    >
      <div className="space-y-6 mb-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-stone-600 max-w-xl">Document each specific bequest, mark fulfillment, attach supporting affidavits and receipts.</p>
          <button
            type="button"
            onClick={addRow}
            disabled={isArchived}
            className="inline-flex items-center rounded-full bg-blue-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-950 disabled:bg-stone-300"
          >
            + Add bequest
          </button>
        </div>
        {loaded && rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-blue-400 bg-blue-50/40 p-5 text-center text-sm text-blue-950">
            <p>No specific bequests yet. Capture each gift now or confirm later directly with counsel.</p>
          </div>
        ) : null}
        {rows.length ? (
          <div className="overflow-x-auto rounded-xl border border-stone-200">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-stone-50 text-[11px] uppercase text-stone-600">
                <tr>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-left">Beneficiary</th>
                  <th className="px-4 py-2 text-left">Amount / item</th>
                  <th className="px-4 py-2 text-left">Method</th>
                  <th className="px-4 py-2 text-left">Fulfilled</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {rows.map((row) => (
                  <Fragment key={row.id}>
                    <tr>
                      <td className="align-top px-4 py-2">
                        <textarea
                          className="mt-2 w-[180px] sm:w-[260px] rounded border border-stone-200 px-2 py-1 text-[13px]"
                          disabled={isArchived}
                          value={row.description}
                          rows={4}
                          onChange={(ev) => mutate(row.id, { description: ev.target.value })}
                        />
                      </td>
                      <td className="align-top px-4 py-2">
                        <input
                          disabled={isArchived}
                          type="text"
                          className="mt-2 w-[160px] rounded border px-2 py-1 text-sm"
                          value={row.beneficiaryName}
                          onChange={(ev) => mutate(row.id, { beneficiaryName: ev.target.value })}
                        />
                      </td>
                      <td className="align-top px-4 py-2">
                        <input
                          disabled={isArchived}
                          type="text"
                          className="mt-2 w-[140px] rounded border px-2 py-1 text-sm"
                          value={row.amountLabel}
                          onChange={(ev) => mutate(row.id, { amountLabel: ev.target.value })}
                        />
                      </td>
                      <td className="align-top px-4 py-2">
                        <select
                          disabled={isArchived}
                          value={row.method}
                          className="mt-2 rounded border px-2 py-1 bg-white max-w-[10rem]"
                          onChange={(ev) =>
                            mutate(row.id, { method: ev.target.value as (typeof METHOD_OPTS)[number] })
                          }
                        >
                          {METHOD_OPTS.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </td>
                      <td className="align-middle px-4 py-2">
                        <input
                          disabled={isArchived}
                          type="checkbox"
                          className="h-5 w-5 accent-blue-950"
                          checked={row.fulfilled}
                          onChange={(ev) => mutate(row.id, { fulfilled: ev.target.checked })}
                        />
                      </td>
                      <td className="align-top px-2 py-4">
                        <button
                          disabled={isArchived}
                          type="button"
                          className="text-xs font-semibold text-red-700 disabled:text-stone-400"
                          onClick={() => removeRow(row.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </SettlementStepPage>
  );
}
